/**
 * F 331 — THE WIRING.
 *
 * Everything the dashboard needs comes from the graph: `registry.*` verdict
 * nodes (F 329), `capability.*` nodes (F 330), `cooldown.*` nodes, and the two
 * node families F 331 writes itself — `override.*` and `research.*`.
 *
 * The component renders. This hook decides. Keeping them apart means the whole
 * view can be tested without React and without a browser, which is the same
 * discipline F 324 used when it refused to let a settings surface be untestable.
 */
import { useMemo } from 'react';
import type { SettingStore } from '../../../core/settings/SettingStore';
import type { ModelCapabilities } from '../../../core/registry/capabilities';
import type { CooldownRecord } from '../../../core/registry/cooldown';
import {
  buildEngineState, type EngineState, type ProviderSlot, type UsageEntry,
} from '../../../core/registry/engineState';
import {
  KNOWN_PROVIDERS, buildSlots, makeOverride, powerUpKey, overrideNodeValue,
  researchKey, type OverrideRecord, type PowerUpOffer, type ResearchFinding,
  type ProviderDeclaration,
} from '../../../core/registry/pipeline';

/**
 * F 331 — POWER-UP OFFER BOOK.
 *
 * These are PAID models by catalogue name. They are NOT probed, and the UI must
 * not imply otherwise: the offer is "you could spend money on this", not "we
 * measured this and it is better". The rationale strings say which measured gap
 * the offer would close.
 */
export const POWER_UP_OFFERS: PowerUpOffer[] = [
  {
    capability: 'reasoning', paidModel: 'claude-sonnet-5', provider: 'anthropic',
    estimatedUsdPerMTokens: 15,
    rationale: 'the free tier reasons thinly; a paid model reasons heavily',
  },
  {
    capability: 'vision', paidModel: 'gpt-4.1', provider: 'openai',
    estimatedUsdPerMTokens: 10,
    rationale: 'no connected provider is strong at vision',
  },
  {
    capability: 'embeddings', paidModel: 'text-embedding-3-small', provider: 'openai',
    estimatedUsdPerMTokens: 0.02,
    rationale: 'nothing in the engine measures embeddings at all',
  },
];

export const GRAPH_PREFIXES = {
  capability: 'capability.',
  cooldown: 'cooldown.',
  override: 'override.',
  research: 'research.',
} as const;

function readJson(store: SettingStore | null, key: string): Record<string, unknown> | null {
  const v = store?.getSetting(key);
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/**
 * F 362 — MEASURED FIX. The capability list comes from the AGGREGATE node.
 *
 * The original reader looked for per-model nodes: any setting whose key starts
 * with `capability.` AND whose value has a top-level `model` string. Measured
 * against the live graph, that matches NOTHING. The graph carries ONE node,
 * `capability.engine`, whose value is `{ caps: ModelCapabilities[], slots }` —
 * seven models in one array, not seven nodes.
 *
 * So the reader returned `[]`, the catalog rendered zero rows, and the reason
 * looked like "the probe never ran". It ran. The reader was reading a shape
 * the writer does not produce. A validator that reads a different artefact
 * than the one that was written passes for free, and fails silently.
 *
 * Both shapes are still accepted: the aggregate node is authoritative, and a
 * per-model node is still honoured so a future writer that decides to explode
 * the array into nodes keeps working without a change here.
 */
export function readCapabilities(store: SettingStore | null): ModelCapabilities[] {
  const out: ModelCapabilities[] = [];
  const seen = new Set<string>();
  for (const s of store?.allSettings() ?? []) {
    if (!s.key.startsWith(GRAPH_PREFIXES.capability)) continue;
    const v = s.value as Record<string, unknown> | undefined;
    if (!v || typeof v !== 'object' || Array.isArray(v)) continue;

    // Authoritative shape: { caps: [...] } on one aggregate node.
    if (Array.isArray(v.caps)) {
      for (const c of v.caps as Record<string, unknown>[]) {
        if (!c || typeof c.model !== 'string' || typeof c.provider !== 'string') continue;
        const key = `${c.provider}/${c.model}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(c as unknown as ModelCapabilities);
      }
      continue;
    }

    // Legacy / alternative shape: one node per model.
    if (typeof v.model === 'string' && typeof v.provider === 'string') {
      const key = `${v.provider}/${v.model}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v as unknown as ModelCapabilities);
    }
  }
  return out;
}

/**
 * F 362 — exported so the AI Engine tab can hand the SAME cooldown map to the
 * model catalog. The table and the pie must read one source of truth; if the
 * catalog re-read the nodes itself the two could disagree about whether a
 * model is benched.
 */
export function readCooldowns(store: SettingStore | null): Map<string, CooldownRecord> {
  const m = new Map<string, CooldownRecord>();
  for (const s of store?.allSettings() ?? []) {
    if (!s.key.startsWith(GRAPH_PREFIXES.cooldown)) continue;
    const v = s.value as Record<string, unknown> | undefined;
    if (!v || typeof v.provider !== 'string' || typeof v.model !== 'string') continue;
    m.set(`${v.provider}::${v.model}`, v as unknown as CooldownRecord);
  }
  return m;
}

export type { ResearchFinding, PowerUpOffer, ProviderDeclaration };
export { KNOWN_PROVIDERS };

export function readOverrides(store: SettingStore | null): OverrideRecord[] {
  const out: OverrideRecord[] = [];
  for (const s of store?.allSettings() ?? []) {
    if (!s.key.startsWith(GRAPH_PREFIXES.override)) continue;
    const v = s.value as Record<string, unknown> | undefined;
    if (v && typeof v.expiresAt === 'number' && typeof v.paidModel === 'string') {
      out.push(v as unknown as OverrideRecord);
    }
  }
  return out;
}

export function readFindings(store: SettingStore | null): Record<string, ResearchFinding> {
  const out: Record<string, ResearchFinding> = {};
  for (const s of store?.allSettings() ?? []) {
    if (!s.key.startsWith(GRAPH_PREFIXES.research)) continue;
    const v = s.value as Record<string, unknown> | undefined;
    if (v && typeof v.provider === 'string') out[v.provider] = v as unknown as ResearchFinding;
  }
  return out;
}

/**
 * RPM headroom, as recorded by the last probe that reported it. A model with no
 * recorded headroom stays null and renders "not reported" — the dashboard must
 * not invent a percentage to fill the ring.
 */
export function readRpmRemaining(
  store: SettingStore | null,
  now: number,
): Map<string, number | null> {
  // Headroom is a COUNT of remaining requests, which F 330 recorded on the
  // verdict node rather than on the capability node. It is read from the raw
  // node so this module does not have to widen ModelCapabilities with a field
  // the classifier never set.
  const m = new Map<string, number | null>();
  for (const s of store?.allSettings() ?? []) {
    const v = s.value as Record<string, unknown> | undefined;
    if (!v || typeof v.provider !== 'string' || typeof v.model !== 'string') continue;
    if (s.key.startsWith(GRAPH_PREFIXES.capability)) {
      const pct = v.rpmRemainingPct;
      m.set(`${v.provider}::${v.model}`, typeof pct === 'number' ? pct : null);
    }
  }
  void now;
  return m;
}

export function readUsage(store: SettingStore | null): UsageEntry[] {
  const key = 'pipeline.usageLedger';
  const v = store?.getSetting(key);
  return Array.isArray(v) ? (v as UsageEntry[]) : [];
}

export interface AiEngineInputs {
  store: SettingStore | null;
  now?: number;
  window?: 'today' | 'week';
  declarations?: ProviderDeclaration[];
}

export function buildEngineStateFromGraph(input: AiEngineInputs): EngineState {
  const now = input.now ?? Date.now();
  const caps = readCapabilities(input.store);
  const overrides = readOverrides(input.store);
  const findings = readFindings(input.store);
  const connected = [...new Set(caps.map((c) => c.provider))];
  const slots: ProviderSlot[] = buildSlots(input.declarations ?? KNOWN_PROVIDERS, connected, findings);
  return buildEngineState({
    caps,
    cooldowns: readCooldowns(input.store),
    slots,
    rpmRemaining: readRpmRemaining(input.store, now),
    usage: readUsage(input.store),
    now,
    window: input.window ?? 'today',
  });
}

/** Write a Power Up. The node carries its own 24-hour expiry. */
export function writePowerUp(
  store: SettingStore,
  offer: PowerUpOffer,
  now: number,
): { key: string; override: OverrideRecord; ok: boolean } {
  const override = makeOverride(offer, now);
  const key = powerUpKey(offer.provider, offer.paidModel);
  const res = store.setSetting({
    key,
    value: overrideNodeValue(override, now),
    valueType: 'json',
    scope: 'global',
    scopeId: null,
    operatorApproved: true,
  });
  return { key, override, ok: res.ok };
}

/** Write an investigation result. */
export function writeResearch(
  store: SettingStore,
  finding: ResearchFinding,
): { key: string; ok: boolean } {
  const key = researchKey(finding.provider);
  const res = store.setSetting({
    key,
    value: finding,
    valueType: 'json',
    scope: 'global',
    scopeId: null,
    operatorApproved: true,
  });
  return { key, ok: res.ok };
}

export function useAiEngine(input: AiEngineInputs): {
  state: EngineState;
  overrides: OverrideRecord[];
  findings: Record<string, ResearchFinding>;
  offers: PowerUpOffer[];
} {
  const state = useMemo(() => buildEngineStateFromGraph(input), [input.store, input.now, input.window]);
  return {
    state,
    overrides: readOverrides(input.store),
    findings: readFindings(input.store),
    offers: POWER_UP_OFFERS,
  };
}

void readJson;